import { Grid, Paper, Typography } from "@material-ui/core";
import { useCallback, useMemo } from "react";
import {
  useBuildingRoomDefaultMedia,
  useBuildingRoomMediaPictures,
  useCreateOrUpdateBuildingMedia,
} from "../../../graphql/hooks";
import { BucketImage } from "../../../graphql/models";
import {
  BuildingMedia,
  BuildingMediaType,
  RoomType,
  roomTypes,
} from "../../../graphql/models/BuildingMedia";
import { useSnackbarMessage } from "../../../hooks";
import { ImagePicker } from "../image-picker";
import { LoadWrapper } from "../load_wrapper";

export interface BuildingMediaRoomEditorProps {
  buildingId: string;
  buildingCode: string;
}

export function BuildingMediaRoomEditor(props: BuildingMediaRoomEditorProps) {
  const { buildingId, buildingCode } = props;

  const { raiseSuccessSnackbar } = useSnackbarMessage();

  const {
    buildingRoomDefaultMediaPicture,
    loading: buildingRoomDefaultMediaPictureLoading,
  } = useBuildingRoomDefaultMedia(buildingId);

  const {
    buildingRoomMediaPictures,
    loading: buildingRoomMediaPicturesLoading,
    refetchBuildingRoomMediaPictures,
  } = useBuildingRoomMediaPictures(buildingId);

  const { createOrUpdate: updateBuildingMedia } =
    useCreateOrUpdateBuildingMedia("UPDATE", {
      onCompleted: () => {
        refetchBuildingRoomMediaPictures();
        raiseSuccessSnackbar("Immagine aggiornata con successo");
      },
    });

  const { createOrUpdate: createBuildingMedia } =
    useCreateOrUpdateBuildingMedia("CREATE", {
      onCompleted: () => {
        refetchBuildingRoomMediaPictures();
        raiseSuccessSnackbar("Immagine aggiornata con successo");
      },
    });

  const loading = useMemo(() => {
    return (
      buildingRoomDefaultMediaPictureLoading || buildingRoomMediaPicturesLoading
    );
  }, [
    buildingRoomDefaultMediaPictureLoading,
    buildingRoomMediaPicturesLoading,
  ]);

  const triggerUpdateRoomImage = useCallback(
    (
      roomType: RoomType,
      media: BuildingMedia | undefined,
      newImage: BucketImage
    ) => {
      if (media) {
        updateBuildingMedia({
          ...media,
          image: {
            url: newImage.url,
          },
        });
      } else {
        createBuildingMedia({
          building: {
            id: buildingId,
            code: buildingCode,
          },
          mediaType: BuildingMediaType.ROOM_PICTURE,
          roomMetadata: {
            roomType,
          },
          image: {
            url: newImage.url,
          },
        });
      }
    },
    [buildingCode, buildingId, createBuildingMedia, updateBuildingMedia]
  );

  const triggerUpdateDefaultRoomImage = useCallback(
    (media: BuildingMedia | undefined, newImage: BucketImage) => {
      if (media) {
        updateBuildingMedia({
          ...media,
          image: {
            url: newImage.url,
          },
        });
      } else {
        createBuildingMedia({
          building: {
            id: buildingId,
            code: buildingCode,
          },
          mediaType: BuildingMediaType.DEFAULT_ROOM_PICTURE,
          image: {
            url: newImage.url,
          },
        });
      }
    },
    [buildingCode, buildingId, createBuildingMedia, updateBuildingMedia]
  );

  const roomListItems = useMemo(() => {
    return Object.entries(roomTypes).map(([roomType, label]) => {
      const firstRoomImage = buildingRoomMediaPictures?.find(
        (v) => v.roomMetadata?.roomType === roomType
      );
      return (
        <Grid container item style={{ padding: "1rem" }} xs={12}>
          <Grid item xs={3}>
            <Typography variant="h5">{label}</Typography>
          </Grid>
          <Grid item xs={9}>
            <ImagePicker
              defaultValue={firstRoomImage?.image?.url}
              onUploaded={(entry) => {
                triggerUpdateRoomImage(
                  roomType as RoomType,
                  firstRoomImage,
                  entry
                );
              }}
              width={"100%"}
              height={200}
            />
          </Grid>
        </Grid>
      );
    });
  }, [buildingRoomMediaPictures, triggerUpdateRoomImage]);

  const defaultImagePicker = useMemo(() => {
    return (
      <ImagePicker
        defaultValue={buildingRoomDefaultMediaPicture?.image?.url}
        onUploaded={(entry) =>
          triggerUpdateDefaultRoomImage(buildingRoomDefaultMediaPicture, entry)
        }
        width={300}
        height={300}
      />
    );
  }, [buildingRoomDefaultMediaPicture, triggerUpdateDefaultRoomImage]);

  return (
    <LoadWrapper loading={loading}>
      <Paper elevation={1} style={{ width: "100%", padding: "1rem" }}>
        <Typography variant="h6">Immagini predefinite per le stanze</Typography>
        <Typography variant="caption">
          Queste immagini verranno utilizzate quando un provider di domotica
          esterno registra l'esistenza di una nuova stanza
        </Typography>
        <Grid container xs={12} style={{ width: "100%" }}>
          {roomListItems}
        </Grid>
        <Grid container xs={12} style={{ width: "100%" }}>
          <Typography variant="caption">
            Se non sarà presente nessuna immagine in quelle indicate sopra in
            fase di creazione di una stanza, verrà utilizzata questa immagine
            predefinita:
          </Typography>
          <br />
          {defaultImagePicker}
        </Grid>
      </Paper>
    </LoadWrapper>
  );
}
